import { useRef } from 'react'
import type { ReactNode } from 'react'
import { useRafCallback } from '@/hooks/use-raf-callback'
import { useReducedMotion } from '@/hooks/use-reduced-motion'

interface TiltCardProps {
  children: ReactNode
  className?: string
  intensity?: number
}

export function TiltCard({ children, className = '', intensity = 10 }: TiltCardProps) {
  const ref = useRef<HTMLDivElement>(null)
  const reduced = useReducedMotion()

  const apply = useRafCallback((rotateX: number, rotateY: number, px: number, py: number) => {
    const element = ref.current
    if (element === null) return
    element.style.setProperty('--tilt-x', `${rotateX.toFixed(2)}deg`)
    element.style.setProperty('--tilt-y', `${rotateY.toFixed(2)}deg`)
    element.style.setProperty('--pointer-x', `${px.toFixed(2)}%`)
    element.style.setProperty('--pointer-y', `${py.toFixed(2)}%`)
  })

  const onPointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (reduced || event.pointerType !== 'mouse') return
    const element = ref.current
    if (element === null) return
    const rect = element.getBoundingClientRect()
    const normalisedX = (event.clientX - rect.left) / rect.width
    const normalisedY = (event.clientY - rect.top) / rect.height
    apply(
      -(normalisedY - 0.5) * intensity,
      (normalisedX - 0.5) * intensity,
      normalisedX * 100,
      normalisedY * 100,
    )
  }

  const reset = () => {
    apply(0, 0, 50, 50)
  }

  return (
    <div
      ref={ref}
      className={`tilt ${className}`.trim()}
      onPointerMove={onPointerMove}
      onPointerLeave={reset}
    >
      <div className="tilt__inner">{children}</div>
      <span className="tilt__glare" aria-hidden="true" />
    </div>
  )
}
